// Simple beep using Web Audio API to avoid external assets
let audioContext: AudioContext | null = null;
let alarmTimer: number | null = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
};

type SoundId = 'classic' | 'soft' | 'bright' | 'pulse' | 'chime';
type SoundStep = { freq: number; duration: number; type: OscillatorType; gap?: number };

const SOUND_LIBRARY: Record<SoundId, SoundStep[]> = {
  classic: [
    { freq: 880, duration: 140, type: 'sine', gap: 90 },
    { freq: 880, duration: 160, type: 'sine' },
  ],
  soft: [
    { freq: 660, duration: 240, type: 'sine', gap: 120 },
    { freq: 520, duration: 240, type: 'sine' },
  ],
  bright: [
    { freq: 1120, duration: 140, type: 'triangle', gap: 80 },
    { freq: 1320, duration: 160, type: 'triangle' },
  ],
  pulse: [
    { freq: 760, duration: 120, type: 'square', gap: 80 },
    { freq: 760, duration: 120, type: 'square', gap: 80 },
    { freq: 760, duration: 120, type: 'square' },
  ],
  chime: [
    { freq: 740, duration: 180, type: 'sine', gap: 60 },
    { freq: 988, duration: 200, type: 'sine', gap: 60 },
    { freq: 1175, duration: 220, type: 'sine' },
  ],
};

const playTone = (frequency: number, volume: number, duration: number, type: OscillatorType) => {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    ctx.resume();
  }

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.type = type;
  oscillator.frequency.value = frequency;
  gainNode.gain.value = Math.max(0.01, volume);

  oscillator.start();
  gainNode.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + duration / 1000);
  oscillator.stop(ctx.currentTime + duration / 1000);
};

const playSequence = (steps: SoundStep[], volume: number) => {
  let offset = 0;
  steps.forEach((step) => {
    const gap = step.gap ?? 0;
    setTimeout(() => playTone(step.freq, volume, step.duration, step.type), offset);
    offset += step.duration + gap;
  });
  return offset;
};

export const playSound = (soundId: SoundId, volume: number = 0.5) => {
  try {
    const steps = SOUND_LIBRARY[soundId];
    playSequence(steps, volume);
  } catch (e) {
    console.error('Audio playback failed', e);
  }
};

export const startAlarm = (soundId: SoundId, volume: number = 0.5) => {
  stopAlarm();
  const steps = SOUND_LIBRARY[soundId];
  const loopMs = playSequence(steps, volume) + 400;
  alarmTimer = window.setInterval(() => {
    playSequence(steps, volume);
  }, loopMs);
};

export const stopAlarm = () => {
  if (alarmTimer) {
    window.clearInterval(alarmTimer);
    alarmTimer = null;
  }
};

export const warmupAudio = () => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
  } catch (e) {
    console.error('Audio warmup failed', e);
  }
};
